import { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Transaction } from '../../../services/types';
import { LION_GREEN } from './OnePageLayout';

// Nearly opaque: while the notice is up, nothing beneath it competes for attention.
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;

  @media (max-width: 575px) {
    padding: 0;
  }
`;

// Three groups set apart by space: what happened, how much to send, what is promised — then the
// actions. A card on tablet and desktop, the whole screen on a phone. It never outgrows the visible
// viewport: on a short screen its content scrolls inside, so both actions stay reachable.
const Modal = styled.div`
  width: 480px;
  max-width: 100%;
  max-height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 20px;
  padding: 44px 40px 32px;
  box-sizing: border-box;
  text-align: center;
  font-family: 'PT Mono', monospace;

  @media (max-width: 575px) {
    width: 100%;
    height: 100%;
    border-radius: 0;
    padding: 32px 20px 24px;

    /* Centres the content while it fits and collapses once it has to scroll. */
    &::before,
    &::after {
      content: '';
      flex: 1 0 0;
    }
  }
`;

const Title = styled.h3`
  margin: 0 0 10px;
  font-size: 17px;
  font-weight: 600;
  line-height: 1.3;
  color: #333;
`;

const SubText = styled.p`
  margin: 0 0 28px;
  font-size: 13px;
  color: #777;
  line-height: 1.5;
`;

const AmountsBlock = styled.div`
  background: rgb(248, 255, 239);
  border-radius: 14px;
  padding: 28px 20px;
  margin-bottom: 28px;
`;

const SendLabel = styled.div`
  margin-bottom: 8px;
  font-size: 13px;
  color: #555;
`;

// Scales with the viewport and may wrap: a long amount with a long currency code must fit 320px.
const NewAmount = styled.div`
  font-size: clamp(22px, 8vw, 30px);
  font-weight: 700;
  line-height: 1.15;
  color: ${LION_GREEN};
  overflow-wrap: anywhere;
`;

const OldAmount = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  font-size: 14px;
  color: #777;
  overflow-wrap: anywhere;
`;

const PromiseText = styled.p`
  margin: 0 0 10px;
  font-size: 13px;
  color: #333;
  font-weight: 500;
  line-height: 1.5;
`;

const ExactText = styled.p`
  margin: 0 0 32px;
  font-size: 12px;
  color: #777;
  line-height: 1.5;
`;

const ContinueBtn = styled.button`
  flex-shrink: 0;
  width: 100%;
  padding: 14px;
  background: ${LION_GREEN};
  color: #fff;
  border: none;
  border-radius: 12px;
  font-family: 'PT Mono', monospace;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.3;
  white-space: normal;
  cursor: pointer;
  transition: opacity 0.15s;
  &:hover { opacity: 0.88; }
  &:disabled { opacity: 0.5; cursor: default; }
`;

const RefuseBtn = styled.button`
  flex-shrink: 0;
  width: 100%;
  margin-top: 8px;
  padding: 12px 8px;
  background: transparent;
  color: #777;
  border: none;
  font-family: 'PT Mono', monospace;
  font-size: 14px;
  line-height: 1.3;
  white-space: normal;
  cursor: pointer;
  &:hover { text-decoration: underline; }
  &:disabled { opacity: 0.5; cursor: default; text-decoration: none; }
`;

interface Props {
  transaction: Transaction;
  /** The client refuses the adjusted amount: the deposit is cancelled. */
  onDecline: () => Promise<void> | void;
}

const OnePageAmountAlert = ({ transaction, onDecline }: Props) => {
  const [open, setOpen] = useState(true);
  const [declining, setDeclining] = useState(false);
  const { t } = useTranslation();

  if (!open) return null;

  // Валюта — из реквизита, а если его в ответе уже нет, из самого тикета. Прибитый рубль
  // подписал бы чужой валютой сумму депозита, который идёт не в рублях.
  const currency = transaction.requisite?.currency || transaction.currency || '';
  const money = (value: number) => `${value.toLocaleString('ru-RU')} ${currency}`.trim();
  const amount = money(transaction.amount);

  const decline = async () => {
    if (declining) return;
    setDeclining(true);
    try {
      await onDecline();
    } finally {
      setDeclining(false);
    }
  };

  // Closed by its buttons only: a tap past the dialog must not read as agreement.
  return (
    <Overlay>
      <Modal role='dialog' aria-modal='true' aria-labelledby='amount-change-title'>
        <Title id='amount-change-title'>{t('amountChange.title')}</Title>
        <SubText>{t('amountChange.reason')}</SubText>
        <AmountsBlock>
          <SendLabel>{t('amountChange.sendLabel')}</SendLabel>
          <NewAmount>{amount}</NewAmount>
          {transaction.oldAmount != null && (
            <OldAmount>{t('amountChange.insteadOf', { amount: money(transaction.oldAmount) })}</OldAmount>
          )}
        </AmountsBlock>
        <PromiseText>{t('amountChange.promise', { amount })}</PromiseText>
        <ExactText>{t('amountChange.exact')}</ExactText>
        <ContinueBtn
          type='button'
          data-sentry-element='amount-alert-dismiss'
          disabled={declining}
          onClick={() => setOpen(false)}
        >
          {t('amountChange.continue')}
        </ContinueBtn>
        <RefuseBtn type='button' data-sentry-element='amount-alert-refuse' disabled={declining} onClick={decline}>
          {t('amountChange.refuse')}
        </RefuseBtn>
      </Modal>
    </Overlay>
  );
};

export default OnePageAmountAlert;
